// copiato da src/components/solitario/IconaTutorial.tsx:16-55 @ cad51507af021388ceb3a5de34eb61259665077d, generalizzato: il livello pigro, la chiave del «gia` visto» e i testi li passa il gioco.
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { BookOpen } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useTutorialVisto } from '@/lib/tutorial/useTutorialVisto';
import type { ChiaveVisto } from '@/lib/tutorial/useTutorialVisto';

export interface PropsIcona {
  /**
   * Il livello del tutorial, come componente PIGRO creato dal gioco a livello di modulo: `const Livello = /*#__PURE__*\/ lazy(() => import('./TutorialX').then(…))`. L'annotazione PURE e`
   * NECESSARIA (misurato dal solitario il 26/10, `src/components/solitario/IconaTutorial.tsx:9-12`): senza, `lazy(...)` resta nell'entry e il chunk esce in produzione. Il `lazy()` non sta qui
   * di proposito: cosi` la prova che il chunk resti fuori dalla produzione la tiene il verify del gioco, dietro il suo flag letterale.
   */
  Livello: ComponentType<{ onChiudi: () => void }>;
  chiave: ChiaveVisto;
  /** L'etichetta del bottone: «Come si gioca», e «Come si gioca — nuovo» finche` non e` stato aperto. */
  nome: string;
  nuovo: string;
  /** Apre il tutorial al montaggio (decisione di tma-orcb, 23:10:24Z: per esempio da un link). La zona NON legge l'URL: lo decide il gioco. */
  apriAllAvvio?: boolean;
}

/** Il ripiego del boundary: non rende niente (vuoto, ma e` un ELEMENTO: v. il ⚠️ sotto, `fallback ?? …`) e dice all'icona che il livello e` caduto, cosi` il tocco dopo rimonta. */
function Caduto({ onCaduto }: { onCaduto: () => void }) {
  useEffect(() => {
    onCaduto();
  }, [onCaduto]);
  return null;
}

/**
 * L'ICONA del tutorial: `BookOpen` come `Button` icona da 32 (area di tocco 44), e un pallino finche` il tutorial non e` stato aperto una volta (`useTutorialVisto(chiave)`). La mette la Hub del
 * gioco, MAI una partita viva. Alla chiusura il fuoco torna sull'icona.
 *
 * Il livello sta dentro un `ErrorBoundary` LOCALE, per tutti i consumatori (nota N1 di tma-ui-orcb sul SÌ di TMA#870): un chunk che non si carica o un render che lancia non butta giu` la pagina del gioco
 * (ne` la rotta, ne` il `errorElement` della radice). ⚠️ Il ripiego e` un ELEMENTO che non rende niente (`Caduto`) e non `null`: `ErrorBoundary` fa `fallback ?? <schermata d'errore a tutto schermo>`, e con `null` la pagina sparirebbe
 * sotto «Something went wrong». Il tutorial non si apre, la pagina e l'icona restano; l'errore si registra (console.error dell'`ErrorBoundary`).
 * Toccare di nuovo l'icona RIPROVA un RENDER che ha lanciato: il ripiego segna `caduto`, la chiave del boundary cambia e il boundary riparte pulito. ⚠️ NON riprova un CHUNK lazy fallito: `Livello` e` un `lazy()` del
 * gioco e React ricorda l'import fallito, quindi ogni rimontaggio rilancia lo stesso errore senza riscaricare (misurato: la factory parte una volta sola su due tocchi, `tests/tutorial-icona-errore.test.tsx`). Per un
 * chunk fallito serve ricaricare la pagina; un retry vero chiederebbe una factory nell'interfaccia (9) o `chunkReload` (debito, v. la PR). Con il livello GIA` aperto e sano un secondo tocco non fa niente (non lo rimonta). Gli `ErrorBoundary` che i giochi hanno gia` attorno al loro livello (Gin, Sette e mezzo) restano, ridondanti: li toglie il loro seggio.
 */
export function IconaTutorial({ Livello, chiave, nome, nuovo, apriAllAvvio = false }: PropsIcona) {
  const { visto, segnaVisto } = useTutorialVisto(chiave);
  const [aperto, setAperto] = useState(apriAllAvvio);
  // a ogni apertura un boundary NUOVO: dopo un errore il tocco sull'icona rimonta il livello invece di restare sul ripiego vuoto
  const [tentativo, setTentativo] = useState(0);
  // il livello e` caduto (il ripiego e` montato): il tocco sull'icona deve rimontarlo; col livello aperto e sano non fa niente
  const [caduto, setCaduto] = useState(false);
  const segnaCaduto = useCallback(() => setCaduto(true), []);
  // aperto al montaggio vale come aperto col tocco: e` visto
  useEffect(() => {
    if (apriAllAvvio) segnaVisto();
  }, [apriAllAvvio, segnaVisto]);
  const radice = useRef<HTMLSpanElement | null>(null);
  const eraAperto = useRef(false);

  const apri = () => {
    segnaVisto();
    if (aperto && !caduto) return;
    setCaduto(false);
    setTentativo((n) => n + 1);
    setAperto(true);
  };
  const chiudi = useCallback(() => setAperto(false), []);

  // alla chiusura il fuoco torna sull'icona
  useEffect(() => {
    if (aperto) {
      eraAperto.current = true;
    } else if (eraAperto.current) {
      eraAperto.current = false;
      radice.current?.querySelector('button')?.focus();
    }
  }, [aperto]);

  return (
    <span ref={radice} className="relative inline-flex shrink-0">
      <Button variant="icona" size={32} tono="pieno" data-azione="tutorial" aria-label={visto ? nome : nuovo} onClick={apri}>
        <BookOpen size={16} aria-hidden="true" />
      </Button>
      {!visto && <span data-pallino-guida aria-hidden="true" className="pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-brand ring-2 ring-bg" />}
      {aperto && (
        <ErrorBoundary key={tentativo} fallback={<Caduto onCaduto={segnaCaduto} />}>
          <Suspense fallback={null}>
            <Livello onChiudi={chiudi} />
          </Suspense>
        </ErrorBoundary>
      )}
    </span>
  );
}
